import Controller, { OnBeforeFetch, OnBeforeSubmit, OnEdit, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EditEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { FetchAPI, FieldAPI, FormAPI, PhoneNumberAPI } from '@philips-internal/oswald-ui-components';
import { Inject } from '@odinjs/odin';
import { BrokerService, HttpResponse, HttpService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { ToastResponse } from '../../interfaces/ToastResponse';
import { showToastNotificationMessage } from '../../utils/toast-notification-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import UserContext from '../../utils/user-context';
import { LocaleTypes } from '../../constants/locale-types';
import { PersonNameTypes } from '../../constants/person-name-types';
import ProfileModalUploadService, { MandatoryAttachmentFieldProps } from '../../services/profile-modal-upload-service';
import { ObjectComparatorUtils } from '../../utils/object-comparator-utils';
import { BS_REFRESH_FORM_COMPL_PESSOA_FISICA } from '../../constants/application-constants';
import { PersonalInformationAliases } from '../../codegen';
import ProfileBaseController from '../profile-base-controller';
import { cast } from '../../utils/cast-helpers';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';

interface State {
  bodyToUpdate: Record<string, Value>;
  oldValues: PersonalInformationFormRecord;
  record: PersonalInformationFormRecord;
}

interface PersonalInformationFormRecord extends Record<string, Value> {
  cdCargo?: {
    cdCargo: number;
    dsCargo: string;
  };
  cdCurp?: string;
  cdNacionalidade?: {
    cdNacionalidade: string;
    domainFrontendExpression: string;
    dsNacionalidade: string;
  }
  cdReligiao?: {
    cdReligiao: number;
    dsReligiao: string;
  };
  cdRfc?: string;
  dsEmail?: string;
  dsOrgaoEmissorCi?: string;
  dtEmissaoCi?: string;
  dtNascimento?: string;
  ieEstadoCivil?: {
    domainFrontendExpression: string;
    dsValorDominio: string;
    vlDominio: string;
  };
  ieSexo?: {
    domainFrontendExpression: string;
    dsValorDominio: string;
    vlDominio: string;
  }
  nmPessoaFisica?: string;
  nmSocial?: string;
  nrCpf?: string;
  nrIdentidade?: string;
  nrSeqPfCompl?: number;
  sgEmissoraCi?: {
    domainFrontendExpression: string;
    dsValorDominio: string;
    vlDominio: string;
  }
}

interface FieldsRequireProof {
  dsDocumento: string;
  ieAttributo: string;
  nrSequenciaDoc: number;
}

interface FieldTypes {
  dsValorNew: string | Value;
  dsValorOld: string | Value;
  nmAtributo: string;
  nrSeqDocumento?: string | Value;
}

enum FIELD_ENTRIES {
  FIELD_EMAIL = 'dsEmail',
}

interface InsertValProps {
  dsEmail: string;
  ieTipoComplemento: { vlDominio: number };
}

@Controller({ component: 'profile-layout', fragment: 'personal-information' })
export default class PersonalInformationController extends ProfileBaseController {
  state: State = {
    bodyToUpdate: {},
    oldValues: {} as PersonalInformationFormRecord,
    record: {} as PersonalInformationFormRecord,
  };

  @Inject('profile-form')
    personDataFormEditable!: FormAPI;

  @Inject('Id-number-field')
    fieldIdNumber!: FieldAPI;

  @Inject('main-person-name-field-1')
    mainPersonName1Api!: FieldAPI;

  @Inject('main-person-name-field-2')
    mainPersonName2Api!: FieldAPI;

  @Inject('main-person-name-field-3')
    mainPersonName3Api!: FieldAPI;

  @Inject('main-person-name-field-4')
    mainPersonName4Api!: FieldAPI;

  @Inject('main-person-name-field-5')
    mainPersonName5Api!: FieldAPI;

  @Inject('social-person-name-field-1')
    socialPersonName1Api!: FieldAPI;

  @Inject('social-person-name-field-2')
    socialPersonName2Api!: FieldAPI;

  @Inject('social-person-name-field-3')
    socialPersonName3Api!: FieldAPI;

  @Inject('social-person-name-field-4')
    socialPersonName4Api!: FieldAPI;

  @Inject('social-person-name-field-5')
    socialPersonName5Api!: FieldAPI;

  @Inject('civil-name-field')
    civilNameApi!: FieldAPI;

  @Inject('social-name-field')
    socialNameApi!: FieldAPI;

  @Inject('CPF-number-field')
    fieldCpfNumberApi!: FieldAPI;

  @Inject('insuarance-field')
    fieldInsuarance!: FieldAPI;

  @Inject('curp-field')
    fieldCurpApi!: FieldAPI;

  @Inject('rfc-field')
    fieldRfcApi!: FieldAPI;

  @Inject('issuer-field')
    fieldIssuerApi!: FieldAPI;

  @Inject('profile-data-fetch')
    fetchPersonalInformationFormApi!: FetchAPI;

  @OnMount(PersonalInformationAliases.PROFILE_FORM)
  onMountProfileForm(event: MountEvent): void {
    const formApi = event.target as FormAPI;
    super.setContextVariablesForTaskModule();
    super.listenWhetherProfileEditingIsEnabled(formApi);
  }

  @OnMount(PersonalInformationAliases.PHONE_NUMBER)
  handlePhoneNumberLoading(event: MountEvent): void {
    const phoneCountry = LanguageService.getCurrentLanguage().split('-')[1].toLocaleUpperCase();
    const phoneNumberApi = event.target as PhoneNumberAPI;
    phoneNumberApi.setCountry(cast(phoneCountry));
  }

  @OnEdit({ target: ['profile-form'] })
  onEdit(event: EditEvent): void {
    const formApi = event.target as FormAPI;
    this.state.oldValues = formApi.getRecord();
  }

  @OnMount('profile-data-fetch')
  handlePersonalInformationFetchLoading(event: MountEvent): void {
    const fetchApi = event.target as FetchAPI;
    this.refreshFormIfComplPessoaFisicaChanged(fetchApi);
  }

  private refreshFormIfComplPessoaFisicaChanged(fetchApi: FetchAPI): void {
    BrokerService.subscribeToTopic(BS_REFRESH_FORM_COMPL_PESSOA_FISICA, () => {
      fetchApi.forceFetch();
    });
  }

  @OnBeforeFetch({ target: PersonalInformationAliases.PROFILE_DATA_FETCH})
  handleBeforeFetchProfileData(): void {
    this.fetchPersonalInformationFormApi.setParameters({ personCode: UserContext.patientId });
  }

  @OnBeforeSubmit({ target: ['profile-form'] })
  onBeforeSubmitEvent(event: SubmitEvent): void {
    const campos: FieldTypes[] = [];
    const insertVal: InsertValProps[] = [];
    const newValues = event.record as PersonalInformationFormRecord;
    const oldValues = this.state.oldValues;
    const body = { campos, insertVal };
    const updatedFields: string[] = [];

    const changedFields = ObjectComparatorUtils.getChangedFields(newValues, oldValues);
    changedFields.forEach(field => {
      const { newValue, oldValue, propertyName } = field;
      if (propertyName === FIELD_ENTRIES.FIELD_EMAIL && !newValues.nrSeqPfCompl) {
        body.insertVal.push({
          dsEmail: String(newValue.rawValue || ''),
          ieTipoComplemento: { vlDominio: 1 }
        });
      } else {
        this.updatedFields(updatedFields, propertyName);
        body.campos.push({
          dsValorNew: newValue
            ? newValue.vlDominio
            || newValue.cdCargo
            || newValue.cdReligiao
            || newValue.cdNacionalidade
            || newValue.rawValue : '',
          dsValorOld: oldValue
            ? oldValue.vlDominio
            || oldValue.cdCargo
            || oldValue.cdReligiao
            || oldValue.cdNacionalidade
            || oldValue.rawValue : '',
          nmAtributo: propertyName,
        });
      }
    });

    if (body.campos.length === 0 && body.insertVal.length === 0) {
      return;
    }

    if(body.campos.length > 0) {
      HttpService.get(APPLICATION_ENDPOINTS.GET_FIELDS_REQUIRE_PROOF).then((resp: HttpResponse<unknown>) => {
        const { body: bodyResponse } = resp as HttpResponse<FieldsRequireProof[]>;
        const fieldsRequireProof = bodyResponse && bodyResponse
          .filter(doc => updatedFields.some((updatedField) => updatedField === doc.ieAttributo.replace(/_/g, '')))
          .map(doc => ({
            attribute: doc.ieAttributo,
            key: doc.nrSequenciaDoc?.toString(),
            required: true,
            value: doc.dsDocumento,
          } as MandatoryAttachmentFieldProps
          ));

        if (fieldsRequireProof && fieldsRequireProof.length > 0) {
          ProfileModalUploadService.open({
            bodyToUpdate: body,
            mandatoryAttachmentsFields: fieldsRequireProof ,
            onConfirmUpload: e => this.sendUpdateRequest({campos: e.record.campos, insertVal: body.insertVal}),
          });
        } else {
          this.sendUpdateRequest(body);
        }
      });
    } else if (body.insertVal.length > 0) {
      this.sendUpdateRequest(body);
    }
  }

  sendUpdateRequest(body: Record<string, Value>): void {
    this.personDataFormEditable.setLoading(true);
    PatientHttpService.post<ToastResponse>(APPLICATION_ENDPOINTS.POST_PROFILE_INFORMATION, { patientId: UserContext.patientId }, { body })
      .then((response) => {
        const insertVal = body?.insertVal as [] || [];
        if(insertVal.length > 0) {
          this.fetchPersonalInformationFormApi.forceFetch();
        }
        this.handleResponse(response);
      }).catch(({ response }: HttpServiceError<ToastResponse>) => {
        this.handleResponse(response);
      }).finally(() => {
        this.personDataFormEditable.setLoading(false);
      });
  }

  handleResponse(resp?: HttpResponse<ToastResponse>): void {
    if (resp?.body) {
      showToastNotificationMessage(resp.body);
    }
  }

  updatedFields(updatedFields: string[], propertyName: string ):  void {
    switch (propertyName.toUpperCase()) {
      case PersonNameTypes.MAIN_GIVEN_NAME:
      case PersonNameTypes.SOCIAL_GIVEN_NAME:
        updatedFields.push(PersonNameTypes.GIVEN_NAME);
        break;
      case PersonNameTypes.MAIN_FAMILY_NAME:
      case PersonNameTypes.SOCIAL_FAMILY_NAME:
        updatedFields.push(PersonNameTypes.FAMILY_NAME);
        break;
      case PersonNameTypes.MAIN_COMPONENT_NAME1:
      case PersonNameTypes.SOCIAL_COMPONENT_NAME1:
        updatedFields.push(PersonNameTypes.COMPONENT_NAME1);
        break;
      case PersonNameTypes.MAIN_COMPONENT_NAME2:
      case PersonNameTypes.SOCIAL_COMPONENT_NAME2:
        updatedFields.push(PersonNameTypes.COMPONENT_NAME2);
        break;
      case PersonNameTypes.MAIN_COMPONENT_NAME3:
      case PersonNameTypes.SOCIAL_COMPONENT_NAME3:
        updatedFields.push(PersonNameTypes.COMPONENT_NAME3);
        break;
      default:
        updatedFields.push(propertyName.toUpperCase());
        break;
    }
  }

  @OnMount({ target: ['form-panel'] })
  onMountAllForms(): void {
    const language = LanguageService.getCurrentLanguage();
    switch (language) {
      case LocaleTypes.MEXICO:
        this.localeMexicoVisibility();
        break;
      case LocaleTypes.COLOMBIA:
        this.localeColombiaVisibility();
        break;
      case LocaleTypes.ARGENTINA:
        this.localeArgentinaVisibility();
        break;
      default:
        this.localeDefaultVisibility();
        break;
    }
  }

  localeDefaultVisibility(): void {
    this.setVisibility(true, this.fieldCpfNumberApi, this.civilNameApi, this.socialNameApi, this.fieldIdNumber);
    this.setVisibility(false, this.mainPersonName1Api, this.mainPersonName2Api, this.mainPersonName3Api, this.mainPersonName4Api, this.fieldCurpApi, this.fieldRfcApi);
  }

  localeMexicoVisibility(): void {
    this.setVisibility(true, this.mainPersonName1Api, this.mainPersonName2Api, this.mainPersonName3Api, this.socialNameApi, this.fieldCurpApi, this.fieldRfcApi);
    this.setVisibility(false, this.civilNameApi, this.mainPersonName4Api, this.fieldCpfNumberApi, this.fieldIdNumber);
    this.setProperties(this.mainPersonName1Api, MessageService.translate('patient.profile-name'), 'mainGivenName', true);
    this.setProperties(this.mainPersonName2Api, MessageService.translate('patient.profile.locale.colombia.family-name'), 'mainFamilyName', true);
    this.setProperties(this.mainPersonName3Api, MessageService.translate('patient.profile.main.component-name-3'), 'mainComponentName1', false);
  }

  localeColombiaVisibility(): void {
    this.setVisibility(true, this.mainPersonName1Api, this.mainPersonName2Api, this.mainPersonName3Api, this.mainPersonName4Api, this.fieldCpfNumberApi, this.fieldIdNumber);
    this.setVisibility(false, this.civilNameApi, this.socialNameApi, this.fieldCurpApi, this.fieldRfcApi);
    this.setProperties(this.mainPersonName1Api, MessageService.translate('patient.profile.main.given-name'), 'mainGivenName', true);
    this.setProperties(this.mainPersonName2Api, MessageService.translate('patient.profile.main.component-name-1'), 'mainComponentName1', false);
    this.setProperties(this.mainPersonName3Api, MessageService.translate('patient.profile.locale.colombia.family-name'), 'mainFamilyName', true);
    this.setProperties(this.mainPersonName4Api, MessageService.translate('patient.profile.locale.colombia.component-name-2'), 'mainComponentName2', false);
  }

  localeArgentinaVisibility(): void {
    this.setVisibility(false, this.fieldCpfNumberApi, this.fieldIssuerApi);
  }

  setVisibility(visible: boolean, ...linkedFields: FieldAPI[]): void {
    linkedFields.forEach(e => e.setVisible(visible));
  }

  setProperties(linkedField: FieldAPI, label: string, entry: string, mandatory: boolean): void {
    linkedField.setLabel(label);
    linkedField.setEntry(entry);
    if(mandatory) {
      const required = {
        'name': 'required',
        'options': {}
      }
      linkedField.addValidator(required);
    }
  }
}
